import { FunctionComponent, useEffect, useState } from "react"
import { Alert, Button, Col, Modal, Row } from "react-bootstrap"
import Container from "react-bootstrap/Container"
import { useNavigate, useParams } from "react-router-dom"
import RoleForm from "../components/RoleForm"
import { Role } from "../data/models.interface"
import { useCreateRoleQuery } from "../hooks/queries/useCreateRoleQuery"
import { useDeleteRoleQuery } from "../hooks/queries/useDeleteRoleQuery"
import { useGetPassesQuery } from "../hooks/queries/useGetPassesQuery"
import { useGetRolesQuery } from "../hooks/queries/useGetRolesQuery"
import { useUpdateRoleQuery } from "../hooks/queries/useUpdateRoleQuery"
import { Parameters } from "../hooks/useLazyQuery"

interface IRolePage {}
const RolePage: FunctionComponent<IRolePage> = () => {
	const params = useParams()
	const roleId = params.roleId
	//
	const rolesQuery = useGetRolesQuery()
	const roleLoaded = rolesQuery.data?.roles?.find((s) => s.id === roleId)
	const createRoleQuery = useCreateRoleQuery()
	const updateRoleQuery = useUpdateRoleQuery(roleId || "error")
	const deleteRoleQuery = useDeleteRoleQuery(roleId || "error")
	const saveQuery = roleId === "new" ? createRoleQuery : updateRoleQuery
	const passesQuery = useGetPassesQuery()
	//
	const [role, setRole] = useState<Role>()
	const [touched, setTouched] = useState(new Map<string, boolean>())
	const [validationErrors, setValidationErrors] = useState(new Map<string, string>())
	//
	useEffect(() => {
		// only init state once
		if (role == null) {
			if (roleId === "new") setRole({} as Role)
			else if (roleLoaded) setRole(roleLoaded)
		}
	}, [roleId, roleLoaded, role])
	// error message
	const invalidIdError = roleId !== "new" && rolesQuery.data != null && roleLoaded == null ? "No Role found" : undefined
	const error = createRoleQuery.error || updateRoleQuery.error || rolesQuery.error || invalidIdError
	// loading layout
	const loading = !passesQuery.data || !role
	if (loading) return <LoadingLayout error={error} />
	// normal layout
	return (
		<Container>
			<h2 className="text-center mt-4">Role</h2>
			{error && <Alert variant={"warning"}>{error}</Alert>}
			<RoleForm
				role={role}
				setRole={setRole}
				touched={touched}
				setTouched={setTouched}
				validationErrors={validationErrors}
				setValidationErrors={setValidationErrors}
				passes={passesQuery.data?.passes!}
			/>
			<Row>
				<Col xs="auto">
					<BackButton />
				</Col>
				{/* Hide delete for new passes */}
				{roleId !== "new" && (
					<Col xs="auto">
						<DeleteButton query={deleteRoleQuery.query} loading={deleteRoleQuery.loading} />
					</Col>
				)}
				<Col></Col>
				<Col xs="auto">
					<SaveButton
						role={role}
						setTouched={setTouched}
						validationErrors={validationErrors}
						query={saveQuery.query}
						loading={saveQuery.loading}
					/>
				</Col>
			</Row>
		</Container>
	)
}
export default RolePage

const LoadingLayout = ({ error }: { error?: string }) => {
	return (
		<Container style={{ maxWidth: 500 }}>
			<h2 className="text-center mt-4">Role</h2>
			{error && <Alert variant={"warning"}>{error}</Alert>}
			<div className="text-center">Loading</div>
		</Container>
	)
}

const BackButton = () => {
	const navigate = useNavigate()
	return (
		<Button variant="secondary" className="mb-4" onClick={() => navigate(-1)}>
			Back
		</Button>
	)
}

interface ISaveButton {
	role: Role
	setTouched: (value: Map<string, boolean>) => void
	validationErrors: Map<string, string>
	query: (role: Parameters<Role>) => Promise<void>
	loading: boolean
}
const SaveButton = ({ role, setTouched, validationErrors, query, loading }: ISaveButton) => {
	const navigate = useNavigate()
	const save = async () => {
		// show errors on all fields
		const touchMap = new Map<string, boolean>()
		touchMap.set("name", true)
		setTouched(touchMap)
		//
		try {
			const errorCount = Array.from(validationErrors).filter((e) => e?.length > 0).length
			if (errorCount === 0) {
				await query({ body: role })
				navigate(-1)
			}
		} catch (e) {
			return
		}
	}
	return (
		<Button variant="primary" className="mb-4" disabled={loading} onClick={save}>
			Save
		</Button>
	)
}

interface IDeleteButton {
	query: () => Promise<void>
	loading: boolean
}
const DeleteButton = ({ query, loading }: IDeleteButton) => {
	const [show, setShow] = useState(false)
	const navigate = useNavigate()

	const onConfirm = async () => {
		try {
			await query()
			setShow(false)
			navigate(-1)
		} catch (e) {
			// swallow; hook exposes error for display in page
		}
	}

	return (
		<>
			<Button variant="danger" className="mb-4" disabled={loading} onClick={() => setShow(true)}>
				Delete
			</Button>

			<Modal show={show} onHide={() => setShow(false)} centered>
				<Modal.Header closeButton>
					<Modal.Title>Confirm Delete</Modal.Title>
				</Modal.Header>
				<Modal.Body>Are you sure you want to delete this role? This action cannot be undone.</Modal.Body>
				<Modal.Footer>
					<Button variant="secondary" onClick={() => setShow(false)}>
						Cancel
					</Button>
					<Button variant="danger" onClick={onConfirm} disabled={loading}>
						Delete
					</Button>
				</Modal.Footer>
			</Modal>
		</>
	)
}
